if (!customElements.get('video-banner')) {
  // Delay before revealing YouTube after it reports "playing", so its
  // centre play/pause animation has finished underneath the poster.
  const YOUTUBE_REVEAL_DELAY = 400;
  // If YouTube hasn't reported "playing" by then, retry muted (browsers such as
  // iOS Safari block autoplay with sound inside a freshly created iframe).
  const YOUTUBE_FALLBACK_DELAY = 2500;
  const YOUTUBE_HANDSHAKE_INTERVAL = 250;
  const YOUTUBE_HANDSHAKE_MAX_TRIES = 40;

  class VideoBanner extends HTMLElement {
    connectedCallback() {
      this.toggleButton = this.querySelector('[data-video-toggle]');
      if (!this.toggleButton) return;

      this.source = this.dataset.source;
      this.loop = this.dataset.loop === 'true';
      this.muted = this.dataset.muted === 'true';
      this.isPlaying = false;

      if (this.source === 'upload') {
        this.video = this.querySelector('video');
        if (!this.video) return;
        this.video.controls = false;
        this.video.loop = this.loop;
        this.video.muted = this.muted;
        this.video.addEventListener('play', () => {
          this.setPlaying(true);
          this.setVideoVisible(true);
        });
        this.video.addEventListener('pause', () => this.setPlaying(false));
        this.video.addEventListener('ended', () => this.onEnded());
      }

      this.onMessage = this.onMessage.bind(this);
      this.toggleButton.addEventListener('click', () => this.toggle());
    }

    disconnectedCallback() {
      window.removeEventListener('message', this.onMessage);
      clearTimeout(this.revealTimer);
      clearTimeout(this.fallbackTimer);
      clearInterval(this.handshakeTimer);
    }

    toggle() {
      if (this.isPlaying) {
        this.pause();
      } else {
        this.play();
      }
    }

    play() {
      if (this.source === 'upload') {
        this.video.play().catch(() => this.setPlaying(false));
        return;
      }

      if (!this.iframe) {
        this.createYoutubeIframe();
      } else {
        this.youtubeCommand('playVideo');
      }
      this.setPlaying(true);
      this.scheduleYoutubeFallback();
    }

    scheduleYoutubeFallback() {
      clearTimeout(this.fallbackTimer);
      this.fallbackTimer = setTimeout(() => {
        if (!this.isPlaying || this.classList.contains('is-video-visible')) return;

        // Playback with sound was likely blocked: retry muted
        this.youtubeCommand('mute');
        this.youtubeCommand('playVideo');

        // No state messages at all (API handshake failed): reveal anyway
        if (!this.youtubeConnected) this.setVideoVisible(true);
      }, YOUTUBE_FALLBACK_DELAY);
    }

    pause() {
      if (this.source === 'upload') {
        this.video.pause();
        return;
      }
      clearTimeout(this.fallbackTimer);
      this.youtubeCommand('pauseVideo');
      this.setPlaying(false);
    }

    onEnded() {
      // Show the poster again instead of a frozen last frame / end screen
      this.setPlaying(false);
      this.setVideoVisible(false);
    }

    setPlaying(isPlaying) {
      this.isPlaying = isPlaying;
      this.classList.toggle('is-playing', isPlaying);
      this.toggleButton.setAttribute('aria-pressed', String(isPlaying));
      this.toggleButton.setAttribute(
        'aria-label',
        isPlaying ? this.toggleButton.dataset.labelPause : this.toggleButton.dataset.labelPlay
      );
    }

    setVideoVisible(isVisible) {
      clearTimeout(this.revealTimer);
      this.revealTimer = null;
      this.classList.toggle('is-video-visible', isVisible);
    }

    createYoutubeIframe() {
      const id = this.dataset.youtubeId;
      const target = this.querySelector('[data-youtube-target]');
      if (!id || !target) return;

      const params = new URLSearchParams({
        autoplay: '1',
        enablejsapi: '1',
        controls: '0',
        disablekb: '1',
        fs: '0',
        iv_load_policy: '3',
        playsinline: '1',
        rel: '0',
        modestbranding: '1',
        mute: this.muted ? '1' : '0',
        origin: window.location.origin,
      });
      if (this.loop) {
        params.set('loop', '1');
        params.set('playlist', id);
      }

      this.iframe = document.createElement('iframe');
      this.iframe.src = `https://www.youtube.com/embed/${id}?${params.toString()}`;
      this.iframe.title = this.toggleButton.dataset.labelPlay.replace(/^Play video:\s*/, '');
      this.iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
      this.iframe.tabIndex = -1;
      this.iframe.addEventListener('load', () => this.startYoutubeHandshake());

      window.addEventListener('message', this.onMessage);
      target.appendChild(this.iframe);
    }

    // Subscribe to player state updates. The player inside the iframe isn't
    // ready on `load`, so keep asking until it answers (same as YouTube's API).
    startYoutubeHandshake() {
      let tries = 0;
      clearInterval(this.handshakeTimer);
      this.handshakeTimer = setInterval(() => {
        tries += 1;
        if (this.youtubeConnected || tries > YOUTUBE_HANDSHAKE_MAX_TRIES || !this.iframe) {
          clearInterval(this.handshakeTimer);
          return;
        }
        this.iframe.contentWindow.postMessage(
          JSON.stringify({ event: 'listening', id: this.id || 'video-banner', channel: 'widget' }),
          '*'
        );
      }, YOUTUBE_HANDSHAKE_INTERVAL);
    }

    youtubeCommand(func) {
      if (!this.iframe) return;
      this.iframe.contentWindow.postMessage(JSON.stringify({ event: 'command', func, args: [] }), '*');
    }

    onMessage(event) {
      if (!this.iframe || event.source !== this.iframe.contentWindow) return;

      let data;
      try {
        data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      } catch (error) {
        return;
      }

      if (!this.youtubeConnected) {
        this.youtubeConnected = true;
        clearInterval(this.handshakeTimer);
        // If the user paused before the player was ready, honour that
        if (!this.isPlaying) this.youtubeCommand('pauseVideo');
      }

      const state = data && data.info && data.info.playerState;
      if (state === 1) {
        clearTimeout(this.fallbackTimer);
        this.setPlaying(true);
        if (!this.classList.contains('is-video-visible') && !this.revealTimer) {
          this.revealTimer = setTimeout(() => {
            this.revealTimer = null;
            if (this.isPlaying) this.classList.add('is-video-visible');
          }, YOUTUBE_REVEAL_DELAY);
        }
      } else if (state === 2) {
        // Keep the paused frame on screen (poster only returns on end)
        this.setPlaying(false);
      } else if (state === 0) {
        this.onEnded();
      }
    }
  }

  customElements.define('video-banner', VideoBanner);
}
